import { useMemo } from "react"
import { map } from "lodash"
import { useOutletContext } from "react-router-dom"
import { numberFormatter } from "helpers/formatters/numbers"
import { createExposureSingleDateQuery } from "gatherers/single-date/exposure"
import { createExposureMultiDateQuery } from "gatherers/multi-date/exposure"
import CompareGraph from "../renderers/compare-graph"
import { useSelectedMetric } from "../hooks/selected-metric"
import DataIntegratedSection from "./data-integrated-section"

export default function CompareExposure() {
  const context = useOutletContext()
  const { modelMetadata, modelId, startDate, endDate, selectedDate, positionSets, selectedMetric: queryStringSelectedMetric, modelMetrics: availableMetrics } = context

  const selectedMetric = useSelectedMetric(availableMetrics, queryStringSelectedMetric)

  const currentDate = selectedDate || endDate

  const graph = useMemo(() => ({
    id: "graph",
    name: "Graph",
    renderer: CompareGraph,
    endDate,
    // Compare has no intraday mode; pass false explicitly rather than relying on the arg being omitted.
    metrics: map(positionSets, set => createExposureMultiDateQuery(modelId, set.queryPositionSet, set.queryBase, startDate, endDate, false)),
    availableMetrics,
    selectedMetric,
    positionSetsMetadata: positionSets,
    tickFormatter: numberFormatter,
    separator: true,
  }), [positionSets, availableMetrics, selectedMetric, modelId, startDate, endDate])

  const sections = useMemo(
    () => {
      let firstExpandable = true
      return [
        graph,
        ...map(modelMetadata.categories, category => {
          let defaultExpanded
          if (firstExpandable && !category.singleFactor) {
            defaultExpanded = true
            firstExpandable = false
          }

          return {
            id: category.id,
            name: category.label,
            color: category.color,
            defaultExpanded,
            isGroup: true,
            sorted: true,
            metrics: map(positionSets, set => createExposureSingleDateQuery(modelId, set.queryPositionSet, set.queryBase, currentDate, { category: category.id })),
          }
        }),
      ]
    },
    // [TODO] how to improve reference integrity of position sets
    [graph, modelMetadata?.categories, positionSets, modelId, currentDate],
  )

  return sections.map((section, index) => {
    const configKey = `exposure-${section.id}`
    return (
      <DataIntegratedSection
        key={configKey}
        configKey={configKey}
        section={section}
        positionSets={positionSets}
        index={index}
        defaultFormatter={numberFormatter}
      />
    )
  })
}
